import {
  useState, useEffect, useContext, useMemo, useRef, useCallback
} from 'react';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';

import {
  filterRemovedProducts,
  getPriceAdjustment
} from '../utils/product-bundle-utils';

const priceCache = new Map();
const pendingRequests = new Map();

const usePriceAdjustment = (products, loading) => {
  const { isLocalized, storeId } = useStore();
  const experienceContext = useContext(ExperienceContext);
  const { hosts } = experienceContext;
  const { globalCustomConfiguratorsBaseUrl: priceAdjustmentUrl } = hosts;

  const [adjustedPricing, setAdjustedPricing] = useState(null);

  const activeProducts = useMemo(() => {
    if (!products?.length) return [];
    return filterRemovedProducts(products);
  }, [products]);

  const fetchPriceAdjustment = useCallback((productsToFetch, fetchStoreId, fetchUrl) => {
    if (!productsToFetch?.length || !fetchStoreId || !fetchUrl) {
      return;
    }

    const key = `${fetchStoreId}:${productsToFetch
      .map((product) => `${product.itemId}:${product.quantity || 1}`)
      .sort()
      .join(':')}`;

    if (priceCache.has(key)) {
      setAdjustedPricing(priceCache.get(key));
      return;
    }

    if (pendingRequests.has(key)) {
      pendingRequests.get(key)
        .then((result) => {
          setAdjustedPricing(result);
        })
        .catch(() => {});
      return;
    }

    const requestPromise = getPriceAdjustment({
      storeId: fetchStoreId,
      products: productsToFetch,
      url: fetchUrl
    });

    pendingRequests.set(key, requestPromise);

    requestPromise
      .then((result) => {
        priceCache.set(key, result);
        pendingRequests.delete(key);
        setAdjustedPricing(result);
      })
      .catch(() => {});
  }, []);

  const debouncedFetchRef = useRef();

  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce(300, fetchPriceAdjustment);
  }

  useEffect(() => {
    const shouldFetch = activeProducts?.length > 0
      && isLocalized && !loading && storeId && priceAdjustmentUrl;

    if (!shouldFetch) {
      return;
    }

    debouncedFetchRef.current(activeProducts, storeId, priceAdjustmentUrl);
  }, [activeProducts, isLocalized, loading, storeId, priceAdjustmentUrl]);

  return adjustedPricing;
};

export default usePriceAdjustment;
